import { useProjects } from "@features/projects/use-projects";
import { Button, Heading, TextField } from "@radix-ui/themes";
import { Page, usePageTitle } from "@views/layout/layout";
import { useTranslation } from "react-i18next";
import { HomeSideBar } from "../home/index";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ProjectType } from "@features/projects/type";

export const ProjectFormPage = () => {
  let id = useParams<{ id?: string }>().id;
  if (id === "new") id = undefined;
  const { projects } = useProjects();

  const currentProject = projects.data?.find((p) => p.id === id);
  usePageTitle("Photographe.ai - " + currentProject?.name || "Project");

  return (
    <Page verticalCenter={false}>
      <div className="w-full md:flex">
        <div className="">
          <HomeSideBar />
        </div>
        <div className="grow space-y-8">
          {(!id || currentProject?.id) && (
            <ProjectForm project={currentProject} />
          )}
        </div>
      </div>
    </Page>
  );
};

const ProjectForm = (props: { project?: ProjectType }) => {
  const { t } = useTranslation();
  const { projects, upsert } = useProjects();
  const navigate = useNavigate();
  const [name, setName] = useState(props.project?.name || "");
  const { site } = useParams();

  const nameInUse = (projects.data || []).some(
    (a) =>
      a.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase() &&
      a.id !== props.project?.id
  );

  return (
    <div className="px-4 w-full space-y-4 max-w-2xl">
      <Heading size="4">
        {props.project?.id
          ? `Update project '${props.project?.name}'`
          : "New project"}
      </Heading>
      <TextField.Root
        size="3"
        placeholder={t("components.project_form.name_placeholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <AnimatePresence>
        {nameInUse && (
          <motion.div
            className="text-red-500"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            This project name is already in use.
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-x-2">
        {props.project?.id && (
          <Button
            size="3"
            color="gray"
            variant="outline"
            disabled={upsert.isLoading || name.trim() === "" || nameInUse}
            onClick={async () => {
              navigate(`/${site}/project/${props.project?.id}`);
            }}
          >
            {t("general.cancel")}
          </Button>
        )}

        <Button
          size="3"
          disabled={upsert.isLoading || name.trim() === "" || nameInUse}
          onClick={async () => {
            try {
              const id = await upsert.mutateAsync({
                name: name.trim(),
                id: props.project?.id,
              });
              if (id) {
                navigate(`/${site}/project/${id}`);
              }
            } catch (e) {
              console.error("Failed to create project:", e);
            }
          }}
        >
          {props.project?.id ? t("general.save") : t("general.create")}
        </Button>

        {props.project?.id && (
          <Button
            size="3"
            className="!ml-6"
            color="red"
            disabled={upsert.isLoading || name.trim() === "" || nameInUse}
            onClick={async () => {
              if (
                window.confirm(
                  "Are you sure you want to delete this project? This action cannot be undone."
                )
              ) {
                try {
                  await upsert.mutateAsync({
                    is_deleted: true,
                    name: props.project?.name || "Deleted Project",
                    id: props.project?.id,
                  });
                  navigate(`/`);
                } catch (e) {
                  console.error("Failed to remove project:", e);
                }
              }
            }}
          >
            Delete this project
          </Button>
        )}
      </div>
    </div>
  );
};
